import type { ReactNode } from 'react';

/**
 * Icônes au trait, dessinées en SVG dans le code.
 *
 * Pas de bibliothèque : une police d'icônes chargée depuis un CDN n'arrive
 * pas sur une connexion lente, et chaque bouton s'affiche alors vide. Ici,
 * l'icône fait partie du fichier JavaScript — si l'écran s'affiche, elle
 * s'affiche.
 *
 * Trait uniforme de 1.8, couleur héritée (`currentColor`) : l'icône prend la
 * couleur du texte qu'elle accompagne.
 */

const TRACES = {
  tableau: 'M4 4h6v7H4zM14 4h6v4h-6zM14 12h6v8h-6zM4 15h6v5H4z',
  resultats: 'M4 19h16M7 16V10M12 16V6M17 16v-4',
  anomalie: 'M12 4 2.5 20h19L12 4zM12 10v4M12 17h.01',
  ventes: 'M3 4h2l2.4 11h10.2L20 8H6.2M9 20h.01M17 20h.01',
  clients: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 4.5a3.5 3.5 0 0 1 0 6.5M21 20c0-2.6-1.6-4.9-4-5.7',
  retours: 'M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3',
  produits: 'M12 3 20 7.5v9L12 21l-8-4.5v-9L12 3zM4 7.5l8 4.5 8-4.5M12 12v9',
  categories: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  unites: 'M4 9h16v6H4zM7 9v3M10 9v2M13 9v3M16 9v2',
  stock: 'M3 7l9-4 9 4-9 4-9-4zM3 12l9 4 9-4M3 17l9 4 9-4',
  fournisseurs: 'M3 7h11v9H3zM14 10h4l3 3v3h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  utilisateurs: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8',
  boutiques: 'M4 9l1.5-5h13L20 9M4 9h16v11H4zM4 9c0 1.7 1.3 3 3 3s3-1.3 3-3c0 1.7 1.3 3 2 3s3-1.3 3-3c0 1.7 1.3 3 3 3s3-1.3 3-3M10 20v-5h4v5',
  journal: 'M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h7',
  monnaie: 'M3 6h18v12H3zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 9v.01M18 15v.01',
  panier: 'M5 8h14l-1.5 11h-11zM9 8V6a3 3 0 0 1 6 0v2',
  credit: 'M3 6h18v12H3zM3 10h18M7 15h3',
  tendance: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  baisse: 'M3 7l6 6 4-4 8 8M15 17h6v-6',
  plus: 'M12 5v14M5 12h14',
  fleche: 'M9 6l6 6-6 6',
  alerte: 'M12 9v4M12 17h.01M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  sortie: 'M15 4h4v16h-4M10 16l4-4-4-4M14 12H3',
  cloche: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4l2-2zM10 21h4',
  calendrier: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  menu: 'M4 6h16M4 12h16M4 18h16',
  actualiser: 'M3 12a9 9 0 0 1 15.3-6.4M21 12a9 9 0 0 1-15.3 6.4M3 4v5h5M21 20v-5h-5',
  soleil:
    'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42',
  lune: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z',
  ecran: 'M3 5h18v11H3zM8 21h8M12 16v5',
  image: 'M4 5h16v14H4zM9 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM4 16l5-5 4 4 3-3 4 4',
  crayon: 'M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1zM14.5 6.5l3 3',
  imprimante: 'M6 9V4h12v5M6 18H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2M6 14h12v6H6z',
  personne: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8',
  cadenas: 'M6 11V7a6 6 0 0 1 12 0v4M5 11h14v10H5zM12 15v3',
  oeil: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
} as const;

export type NomIcone = keyof typeof TRACES;

export function Icone({
  nom,
  taille = 18,
  className,
}: {
  nom: NomIcone;
  taille?: number;
  className?: string;
}): ReactNode {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d={TRACES[nom]} />
    </svg>
  );
}
